import { useMemo, type SVGAttributes } from 'react'
import { cn } from '../lib/cn'
import { encodeQr, type QrErrorCorrection } from './qr-code-encoder'

/**
 * Código QR de um endereço, para a pessoa apontar a câmera do celular em vez de digitar.
 * Módulos em ink sobre surface, cantos retos, com a margem de quatro módulos que o leitor exige.
 * O código nunca vai sozinho: quem usa põe ao lado o que ele abre, por quanto tempo vale e o
 * endereço copiável, porque nem toda câmera lê. O desenho é feito aqui, sem chamada externa.
 */
export interface QrCodeProps extends Omit<SVGAttributes<SVGSVGElement>, 'children'> {
  /** O texto que o código carrega (um endereço). */
  value: string
  /** Nome acessível: o que a pessoa encontra ao ler o código. */
  label: string
  /** Lado do desenho em px, com a margem. */
  size?: number
  /** Correção de erro: `medium` (padrão) aguenta tela riscada; `low` deixa o código menos denso. */
  errorCorrection?: QrErrorCorrection
}

/** Margem em módulos em volta do código (a "zona de silêncio" do padrão). */
const QUIET_ZONE = 4

export function QrCode({
  value,
  label,
  size = 200,
  errorCorrection = 'medium',
  className,
  ...props
}: QrCodeProps) {
  const modules = useMemo(() => encodeQr(value, { errorCorrection }), [value, errorCorrection])
  // Texto que não cabe em nenhuma versão do código: nada a desenhar. O endereço ao lado continua.
  if (!modules) return null

  const side = modules.length + QUIET_ZONE * 2
  let path = ''
  modules.forEach((row, y) => {
    row.forEach((dark, x) => {
      if (dark) path += `M${x + QUIET_ZONE} ${y + QUIET_ZONE}h1v1h-1z`
    })
  })

  return (
    <svg
      data-slot="qr-code"
      role="img"
      aria-label={label}
      width={size}
      height={size}
      viewBox={`0 0 ${side} ${side}`}
      shapeRendering="crispEdges"
      className={cn('border-hairline block shrink-0 border', className)}
      {...props}
    >
      <rect width={side} height={side} className="fill-surface" />
      <path d={path} className="fill-ink" />
    </svg>
  )
}
